import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, Image as ImageIcon, PencilSimple, X, MagnifyingGlass } from "@phosphor-icons/react";
import { AdminHeader, Field, inputCls, PrimaryButton, GhostButton } from "@/components/admin/parts";
import { adminApi, getErrorMessage, API } from "@/lib/api";

function ModelCheckList({ options, selected, setSelected, search, setSearch, testidPrefix = "" }) {
  const filtered = useMemo(
    () => options.filter((m) => m.toLowerCase().includes(search.trim().toLowerCase())),
    [options, search]
  );
  const toggle = (m) =>
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(m) ? n.delete(m) : n.add(m);
      return n;
    });
  const selectAllShown = () =>
    setSelected((prev) => {
      const n = new Set(prev);
      filtered.forEach((m) => n.add(m));
      return n;
    });
  const clearAll = () => setSelected(new Set());

  return (
    <div>
      <div className="relative mb-2">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-600" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search models…"
          className={`${inputCls} pl-9`}
          data-testid={`${testidPrefix}model-search`}
        />
      </div>
      <div className="flex items-center justify-between mb-2 text-xs">
        <button
          type="button"
          onClick={selectAllShown}
          className="text-[#73bf44] hover:underline font-semibold"
          data-testid={`${testidPrefix}select-all-models`}
        >
          Select all shown ({filtered.length})
        </button>
        <div className="flex items-center gap-3">
          <span className="text-neutral-400 font-semibold" data-testid={`${testidPrefix}selected-count`}>
            {selected.size} selected
          </span>
          {selected.size > 0 && (
            <button type="button" onClick={clearAll} className="text-neutral-500 hover:text-white" data-testid={`${testidPrefix}clear-selection`}>
              Clear
            </button>
          )}
        </div>
      </div>
      <div
        className="max-h-64 overflow-y-auto border border-neutral-800 rounded-lg divide-y divide-neutral-900 bg-black/30"
        data-testid={`${testidPrefix}model-checklist`}
      >
        {filtered.length === 0 ? (
          <div className="text-neutral-600 text-sm py-8 text-center">No models match your search.</div>
        ) : (
          filtered.map((m) => (
            <label
              key={m}
              className="flex items-center gap-3 px-3 py-2 hover:bg-white/5 cursor-pointer text-sm text-neutral-200"
              data-testid={`${testidPrefix}model-option`}
            >
              <input
                type="checkbox"
                checked={selected.has(m)}
                onChange={() => toggle(m)}
                className="accent-[#73bf44] w-4 h-4 shrink-0"
                data-testid={`${testidPrefix}model-checkbox`}
              />
              <span className="truncate">{m}</span>
            </label>
          ))
        )}
      </div>
    </div>
  );
}

function EditPhotoModal({ photo, available, onClose, onSaved }) {
  const options = useMemo(() => {
    const set = new Set([...(photo.models || []), ...available]);
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [photo, available]);
  const [selected, setSelected] = useState(new Set(photo.models || []));
  const [search, setSearch] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (selected.size === 0) {
      toast.error("Select at least one model");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("models", JSON.stringify(Array.from(selected)));
      if (file) fd.append("file", file);
      await adminApi.put(`/admin/model-photos/${photo.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo updated");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="edit-photo-modal">
      <div className="bg-[#0F0F0F] border border-neutral-800 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-800 sticky top-0 bg-[#0F0F0F]">
          <h2 className="font-heading font-semibold text-white flex items-center gap-2">
            <PencilSimple size={18} className="text-[#73bf44]" weight="duotone" /> Edit Model Photo
          </h2>
          <button onClick={onClose} className="text-neutral-500 hover:text-white" data-testid="edit-photo-close">
            <X size={20} />
          </button>
        </div>
        <div className="p-6 space-y-5">
          <div className="flex items-center gap-4">
            <div className="h-24 w-32 rounded-lg overflow-hidden bg-gradient-to-b from-neutral-100 to-neutral-300 shrink-0">
              <img
                src={`${API}/model-photos/${photo.id}/file?t=${encodeURIComponent(photo.updated_at || "")}`}
                alt="current"
                className="h-full w-full object-contain p-1"
              />
            </div>
            <div className="flex-1">
              <Field label="Replace image (optional)">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="text-sm text-neutral-400 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-[#73bf44] file:text-[#050505] file:font-semibold file:cursor-pointer"
                  data-testid="edit-photo-file"
                />
              </Field>
              {file && <p className="text-xs text-[#73bf44] mt-1">New image selected: {file.name}</p>}
            </div>
          </div>
          <div>
            <label className="text-sm text-neutral-400 mb-1.5 block">Assigned models</label>
            <ModelCheckList
              options={options}
              selected={selected}
              setSelected={setSelected}
              search={search}
              setSearch={setSearch}
              testidPrefix="edit-"
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-neutral-800 sticky bottom-0 bg-[#0F0F0F]">
          <GhostButton onClick={onClose} data-testid="edit-photo-cancel">Cancel</GhostButton>
          <PrimaryButton onClick={save} disabled={saving} data-testid="edit-photo-save">
            {saving ? "Saving…" : "Save Changes"}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}

export default function AdminModelPhotos() {
  const [photos, setPhotos] = useState([]);
  const [available, setAvailable] = useState([]);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([adminApi.get("/admin/model-photos"), adminApi.get("/admin/caravan-models")])
      .then(([p, m]) => {
        setPhotos(p.data);
        setAvailable(m.data.available || []);
        setTotal(m.data.total || 0);
      })
      .catch((e) => toast.error(getErrorMessage(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const upload = async () => {
    if (selected.size === 0) { toast.error("Select at least one model"); return; }
    if (!file) { toast.error("Choose an image"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("models", JSON.stringify(Array.from(selected)));
      fd.append("file", file);
      await adminApi.post("/admin/model-photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Photo assigned to ${selected.size} model${selected.size > 1 ? "s" : ""}`);
      setSelected(new Set());
      setFile(null);
      setSearch("");
      const el = document.getElementById("model-photo-file");
      if (el) el.value = "";
      load();
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setUploading(false);
    }
  };

  const del = async (p) => {
    if (!window.confirm(`Remove this photo and unassign it from ${(p.models || []).length} model(s)?`)) return;
    try {
      await adminApi.delete(`/admin/model-photos/${p.id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  const assignedTotal = total - available.length;

  return (
    <div>
      <AdminHeader
        title="Model Photos"
        subtitle="Assign one photo to any number of caravan models — shown on the customer's My Caravan page & chat sidebar when their chassis matches"
      />
      <div className="p-8 space-y-8">
        <div className="bg-[#0F0F0F] border border-neutral-800 rounded-xl p-6 max-w-2xl">
          <h2 className="font-heading font-semibold text-white mb-1 flex items-center gap-2">
            <ImageIcon size={20} className="text-[#73bf44]" weight="duotone" /> Add a Model Photo
          </h2>
          <p className="text-xs text-neutral-500 mb-4">
            Tick every model this photo should cover. Assigned models are hidden here. Tip: to make an
            “image coming soon” placeholder, upload one image and “Select all shown”.
          </p>
          <Field label="Photo (JPG / PNG / WEBP)">
            <input
              id="model-photo-file"
              type="file"
              accept="image/*"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="text-sm text-neutral-400 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-[#73bf44] file:text-[#050505] file:font-semibold file:cursor-pointer"
              data-testid="model-photo-file"
            />
          </Field>
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm text-neutral-400">
                Models still needing a photo{" "}
                <span className="text-neutral-600">({available.length} of {total})</span>
              </label>
            </div>
            {available.length === 0 && !loading ? (
              <div className="text-sm text-[#73bf44] border border-[#73bf44]/30 bg-[#73bf44]/10 rounded-lg px-3 py-2.5">
                Every model has a photo assigned. 🎉 Use the grid below to edit or replace any of them.
              </div>
            ) : (
              <ModelCheckList
                options={available}
                selected={selected}
                setSelected={setSelected}
                search={search}
                setSearch={setSearch}
              />
            )}
          </div>
          <div className="mt-4">
            <PrimaryButton onClick={upload} disabled={uploading || available.length === 0} data-testid="model-photo-upload">
              <Plus size={16} /> {uploading ? "Uploading…" : `Save Photo${selected.size > 0 ? ` (${selected.size} models)` : ""}`}
            </PrimaryButton>
          </div>
        </div>

        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#73bf44]">
              Uploaded Model Photos ({photos.length})
            </div>
            <div className="text-xs text-neutral-600">· {assignedTotal} of {total} models covered</div>
          </div>
          {loading ? (
            <div className="text-neutral-600">Loading…</div>
          ) : photos.length === 0 ? (
            <div className="text-neutral-600 border border-dashed border-neutral-800 rounded-xl py-16 text-center flex flex-col items-center gap-3">
              <ImageIcon size={36} className="text-neutral-700" weight="duotone" /> No model photos yet. Upload one above.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="model-photos-grid">
              {photos.map((p) => (
                <div key={p.id} className="bg-[#141414] border border-neutral-800 rounded-xl overflow-hidden flex flex-col" data-testid="model-photo-card">
                  <div className="h-36 bg-gradient-to-b from-neutral-100 to-neutral-300 flex items-center justify-center">
                    <img
                      src={`${API}/model-photos/${p.id}/file?t=${encodeURIComponent(p.updated_at || "")}`}
                      alt={(p.models || []).join(", ")}
                      className="h-full w-full object-contain p-2"
                    />
                  </div>
                  <div className="p-3 flex flex-col gap-2 flex-1">
                    <div className="flex flex-wrap gap-1.5" data-testid="model-photo-models">
                      {(p.models || []).slice(0, 4).map((m) => (
                        <span key={m} className="text-[0.65rem] font-semibold px-2 py-0.5 rounded-full bg-[#73bf44]/10 text-[#73bf44] border border-[#73bf44]/25 truncate max-w-full">
                          {m}
                        </span>
                      ))}
                      {(p.models || []).length > 4 && (
                        <span className="text-[0.65rem] font-semibold px-2 py-0.5 rounded-full bg-neutral-800 text-neutral-400">
                          +{(p.models || []).length - 4} more
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-between gap-2 mt-auto pt-1">
                      <span className="text-[0.65rem] text-neutral-600">{(p.models || []).length} model(s)</span>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setEditing(p)}
                          data-testid="model-photo-edit"
                          className="p-1.5 rounded-lg text-neutral-500 hover:text-[#73bf44] hover:bg-[#73bf44]/10"
                          title="Edit assignments / replace image"
                        >
                          <PencilSimple size={15} />
                        </button>
                        <button
                          onClick={() => del(p)}
                          data-testid="model-photo-delete"
                          className="p-1.5 rounded-lg text-neutral-500 hover:text-red-400 hover:bg-red-500/10"
                          title="Delete"
                        >
                          <Trash size={15} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {editing && (
        <EditPhotoModal
          photo={editing}
          available={available}
          onClose={() => setEditing(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}
